'use client';

import {
  Box,
  Flex,
  FormLabel,
  freddyTheme,
  NumberStepInput,
  Typography
} from '@coolblue-development/freddy';
import openProductSummaryTrigger from '@coolblue-development/ga4-events/triggers/OptionsRow/openProductSummaryTrigger';
import selectColorAlternativeTrigger from '@coolblue-development/ga4-events/triggers/OptionsRow/selectColorAlternativeTrigger';
import showColorAlternativeEntranceTrigger from '@coolblue-development/ga4-events/triggers/OptionsRow/showColorAlternativeEntranceTrigger';
import selectEnergyLabelInformationTrigger from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/selectEnergyLabelInformationTrigger';
import showEnergyLabelTrigger from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/showEnergyLabelTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import EnergyLabel from '@coolblue-development/next-energy-label/client';
import { MiniProductPage } from '@coolblue-development/next-mini-product-page/client';
import {
  ColorAlternative,
  ColorPicker,
  ProductTitle,
  ProductTitleTitle,
  SelectableBox,
  SquareProductImage
} from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import { useAccessoryProductCardContext } from '@features/add-to-cart-page/components/Sections/AccessoryProductCard/AccessoryProductCard.context';
import { Product } from '@features/add-to-cart-page/typings/Product';
import { USBCChargerIncluded } from '@features/shared/USBCChargerIncluded';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';
import type { RefObject } from 'react';
import { useRef, useState } from 'react';

import { useCustomerContext } from '../../CustomerContext/CustomerContext';

export type ProductOptionCardProps = {
  title: string;
  description: string;
  shortDescription: string;
  slotProducts: Product[];
  disabled?: boolean;
  handleColorChange?: (value: string) => void;
  quantityStepper?: {
    onChange: (params: {
      index: number;
      productId: number;
      value: number;
      shouldDebounce?: boolean;
    }) => void;
    getQuantity: (index: number) => number;
  };
};

const ProductOptionCard = ({
  title,
  description,
  shortDescription,
  slotProducts,
  disabled,
  mainProductId,
  index,
  totalIndices,
  handleColorChange,
  quantityStepper,
  selectedOption
}: ProductOptionCardProps & {
  index: number;
  totalIndices: number;
  mainProductId: number;
  selectedOption: string;
}) => {
  const { t } = useTranslation('webshop');
  const context = useAccessoryProductCardContext();
  const product = context.productAlternatives[context.selectedProductAlternative];
  const ctaRef: RefObject<HTMLInputElement> = useRef(null);
  const { isInternalUser } = useRequestContext();
  const { isBusinessCustomer } = useCustomerContext();
  const { trackEvent } = useAnalytics();
  const productOptionCardQuantityTestId = useTestId('product-option-card-quantity');

  const colorAlternatives: ColorAlternative[] = context.sanitizeColorAlternativesData(
    slotProducts[0]
  );

  const [productQuantity, setProductQuantity] = useState(context.selectedProductQuantity || 1);

  const onSelectColorAlternative = (index: string) => {
    const selectedAlternative = slotProducts[0].colorAlternatives?.[+index];
    if (selectedAlternative?.product?.id) {
      context.setSelectedProductAlternative(selectedAlternative.product.id);
      context.updateSelectedColorAlternatives(slotProducts[0].id, +index);
      handleColorChange?.(`${selectedAlternative.product.id}`);

      trackEvent(
        selectColorAlternativeTrigger({
          index: +index,
          total_indices: (slotProducts[0].colorAlternatives as ColorAlternative[]).length,
          item_id: selectedAlternative.product.id
        })
      );
    }
  };

  const onMiniProductPageOpen = () => {
    trackEvent(openProductSummaryTrigger());
  };

  const onEnergyLabelShow = () => {
    trackEvent(showEnergyLabelTrigger());
  };

  const onEnergyLabelContentClick = (
    content: 'product_information_sheet' | 'energy_label_image'
  ) => {
    trackEvent(
      selectEnergyLabelInformationTrigger({
        content
      })
    );
  };

  const handleQuantityChange = ({
    quantity,
    triggeredFrom
  }: {
    quantity?: number;
    triggeredFrom: 'miniProductPageInput' | 'miniProductPageClose' | 'card';
  }) => {
    if (!quantityStepper || !isBusinessCustomer) return;
    //Check added to not call SelectOption twice when option is added from the mini product page
    const shouldDebounce =
      triggeredFrom === 'card' ||
      (triggeredFrom === 'miniProductPageClose' && product.id === +selectedOption);

    const updatedValue = quantity ?? quantityStepper.getQuantity(index) ?? productQuantity;
    setProductQuantity(updatedValue);

    quantityStepper.onChange({
      index,
      productId: product.id,
      value: updatedValue,
      shouldDebounce
    });
  };

  return (
    <SelectableBox
      value={`${product.id}`}
      disabled={disabled}
      ref={ctaRef}
      data-cy={isInternalUser ? 'options-row-card' : undefined}
      sx={{
        gridColumn: 'span 12',
        gridTemplateColumns: 'min-content',
        '.options-row': {
          '&__image': {
            gridRow: '1 / span 2'
          },
          '&__top-area': {
            gridRow: 1
          }
        },
        [freddyTheme.breakpoint.minWidth('md')]: {
          gap: 0,
          gridColumn: 'span 6',
          gridTemplateRows: 'subgrid',
          gridRow: 'span 2'
        },
        [freddyTheme.breakpoint.minWidth('lg')]: {
          gridColumn: 'span 4'
        }
      }}
    >
      {product.primaryImage && (
        <Box className='options-row__image' mr={400}>
          <SquareProductImage
            alt={title}
            imageId={`${product.primaryImage.id}`}
            size={{ xs: 'medium', lg: 'large' }}
            background='transparent'
          />
        </Box>
      )}
      <Flex direction='column' className='options-row__top-area'>
        <ProductTitle>
          <ProductTitleTitle
            size={{ xs: 'small', md: 'medium' }}
            data-cy={isInternalUser ? 'options-row-card-title' : undefined}
          >
            <MiniProductPage
              onClose={() => handleQuantityChange({ triggeredFrom: 'miniProductPageClose' })}
              productId={product.id}
              subFeature='options_row'
              mainProductId={mainProductId}
              index={index}
              totalIndices={totalIndices}
              ctaRef={ctaRef}
              onOpen={onMiniProductPageOpen}
              onSelectColorAlternative={
                colorAlternatives.findIndex(item => item.productId === product.id) > -1
                  ? value => {
                      onSelectColorAlternative(value);
                    }
                  : undefined
              }
              colorPickerInitialProductId={slotProducts[0].id}
              isBusinessCustomer={isBusinessCustomer}
              quantity={
                quantityStepper && (
                  <Flex alignItems='center' gap={200} flex={1}>
                    <FormLabel> {t('addToCart.quantity')}</FormLabel>
                    <NumberStepInput
                      value={productQuantity}
                      size='small'
                      min={1}
                      max={product.orderQuantityLimit || undefined}
                      name='mini-product-page-quantity'
                      aria-label={t('addToCart.optionsRow.quantity', { productName: title })}
                      onChange={(quantity: number) =>
                        handleQuantityChange({ quantity, triggeredFrom: 'miniProductPageInput' })
                      }
                    />
                  </Flex>
                )
              }
              bypassEnergyLabelSubsidiaryCheck
            >
              {title}
            </MiniProductPage>
          </ProductTitleTitle>
        </ProductTitle>
        {shortDescription && (
          <Typography variant='body' mt={100} mb={0} hideFrom='md'>
            {shortDescription}
          </Typography>
        )}
        {description && (
          <Typography variant='body' mt={100} mb={0} hideUntil='md'>
            {description}
          </Typography>
        )}
        {colorAlternatives.length > 1 && (
          <Box
            mt={300}
            className='options-row__color-picker'
            data-cy={isInternalUser ? 'options-row-color-picker' : undefined}
          >
            <TrackImmediate
              trigger={showColorAlternativeEntranceTrigger({ sub_feature: 'color_collection' })}
            />
            <ColorPicker
              colorAlternatives={colorAlternatives}
              onChange={onSelectColorAlternative}
              value={context.selectedColorAlternatives[slotProducts[0].id]?.toString() || '0'}
            />
          </Box>
        )}
      </Flex>

      <Flex
        direction='column'
        justifyContent='end'
        className='options-row__bottom-area'
        mt={300}
        gap={300}
      >
        {quantityStepper && (
          <NumberStepInput
            {...productOptionCardQuantityTestId}
            value={productQuantity}
            size='small'
            min={1}
            max={product.orderQuantityLimit || undefined}
            name='options-row-quantity'
            aria-label={t('addToCart.optionsRow.quantity', { productName: title })}
            sx={theme => ({
              // TODO: remove when design-system set a default background color for the buttons
              button: { background: theme.color.background.base }
            })}
            onChange={(quantity: number) =>
              handleQuantityChange({ quantity, triggeredFrom: 'card' })
            }
          />
        )}

        {product.energyLabel && (
          <EnergyLabel
            direction='right'
            imageId={Number(product.energyLabel.image?.id)}
            level={product.energyLabel.level}
            onShow={onEnergyLabelShow}
            onContentClick={onEnergyLabelContentClick}
            size='medium'
            specificationUrl={product.energyLabel.specification?.absoluteUrl}
            bypassSubsidiaryCheck
          />
        )}

        {product.hasUsbCChargerIncluded !== null && (
          <USBCChargerIncluded hasUsbCChargerIncluded={product.hasUsbCChargerIncluded} />
        )}

        {product.salesPrice && (
          <>
            <Box>
              <SalesPrice
                variant={isBusinessCustomer ? 'business' : undefined}
                personalPrice={product.personalPrice}
                currentPrice={
                  <SalesPriceCurrent
                    price={product.salesPrice.includingVat}
                    showPricePerItem={isBusinessCustomer}
                    priceExcludingVat={
                      isBusinessCustomer ? product.salesPrice.excludingVat : undefined
                    }
                  />
                }
                {...(product.listPrice?.includingVat && {
                  formerPrice: (
                    <SalesPriceFormer
                      price={product.listPrice.includingVat}
                      prefix={t('orderBlock.salesPrice.prefix')}
                    />
                  )
                })}
              />
            </Box>
          </>
        )}
      </Flex>
    </SelectableBox>
  );
};

export default ProductOptionCard;
